"use client";

/**
 * AdminFormField — 폼 입력 필드 (라벨 + 입력 컨트롤 묶음)
 *
 * 모달/폼에서 "라벨 + input"을 반복 작성하지 않도록 공통화한 것.
 * - required: 라벨 옆에 빨간 * 표시
 * - description / error: 입력 아래 도움말 / 에러 메시지(빨간색, description 대체)
 * - orientation: 라벨 위치 (vertical=위, horizontal=왼쪽)
 *
 * 입력 컨트롤은 두 가지 방식으로 줄 수 있다:
 * 1) children 으로 직접 주입 (Input, AdminSelect, 커스텀 등 무엇이든)
 * 2) type 으로 자동 생성 — "text" | "email" | "password" | "number" | "comma" | "tel" | "bizNum"
 *    | "textarea" | "select" | "switch" | "upload"
 *    (value/onChange 와 함께 사용. comma 는 천 단위 콤마 포맷을 자동 처리)
 *    switch 는 options 의 두 항목([0]=꺼짐, [1]=켜짐)을 토글로 보여준다 (select 대체용, 상태값이 2개일 때).
 *
 * @example
 * // 자동 input (type)
 * <AdminFormField label="이메일" type="email" required value={email} onChange={setEmail} />
 * <AdminFormField label="금액" type="comma" value={amount} onChange={setAmount} />
 * <AdminFormField label="메모" type="textarea" value={memo} onChange={setMemo} />
 * <AdminFormField
 *   label="상태"
 *   type="select"
 *   required
 *   options={[{ value: "A", label: "진행중" }]}
 *   value={status}
 *   onChange={setStatus}
 * />
 * // type="switch": options[0]=꺼짐, options[1]=켜짐 — 상태값이 2개뿐일 때 select 대신 사용
 * // switchProps 로 크기 조절 가능 (기본 md, 옆 input/select 와 톤을 맞추고 싶으면 lg)
 * <AdminFormField
 *   label="상태"
 *   type="switch"
 *   options={[{ value: "00", label: "비활성" }, { value: "01", label: "활성" }]}
 *   value={status}
 *   onChange={setStatus}
 *   switchProps={{ size: "lg" }}
 * />
 * // type="upload": 이미지 1장을 value(문자열 URL) 로 다룬다 (여러 장은 AdminImageUpload 직접 사용)
 * <AdminFormField
 *   label="사업자등록증"
 *   type="upload"
 *   value={form.values.biz_img}
 *   onChange={(url) => form.setValues((v) => ({ ...v, biz_img: url }))}
 *   uploadProps={{ accept: "image/*,.pdf", onFilesAdded: (files) => setBizImg(files[0] ?? null) }}
 * />
 *
 * @example
 * // 커스텀 컨트롤 (children)
 * <AdminFormField label="상태">
 *   <select ...>...</select>
 * </AdminFormField>
 */
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "../../lib/utils";
import { type ColumnFormatType, formatByType } from "../../lib/format";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch, type SwitchProps } from "../ui/switch";
import {
  AdminSelect,
  type AdminSelectHandle,
  type AdminSelectOption,
  type AdminSelectProps,
} from "../select/admin-select";
import {
  AdminImageUpload,
  type AdminImageUploadHandle,
  type AdminImageUploadProps,
} from "../upload/admin-image-upload";

export type AdminFormFieldType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "textarea"
  | "select"
  | "switch"
  | "upload"
  | ColumnFormatType; // "comma" | "tel" | "bizNum" — 표시는 포맷, 저장은 숫자만

/** 입력값을 포맷해서 보여주는 타입들 (저장 시 숫자만 추출) */
const FORMATTED_TYPES: ColumnFormatType[] = ["comma", "tel", "bizNum"];

/** 포맷 타입별 최대 입력 자릿수 (없으면 무제한) */
const MAX_DIGITS: Partial<Record<ColumnFormatType, number>> = {
  tel: 11, // 휴대폰 010xxxxxxxx
  bizNum: 10, // 사업자등록번호
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 타입별 기본 형식 검증. 통과하면 undefined, 아니면 오류 메시지를 반환한다.
 * (빈 값은 required 가 아니면 통과 — 비필수 선택 입력 허용)
 */
function validateValue(
  type: AdminFormFieldType | undefined,
  value: string | number | undefined,
  required?: boolean
): string | undefined {
  const v = String(value ?? "").trim();
  if (!v) return required ? "필수 입력 항목입니다." : undefined;

  switch (type) {
    case "email":
      return EMAIL_RE.test(v) ? undefined : "이메일 형식이 올바르지 않습니다.";
    case "tel": {
      const len = v.replace(/\D/g, "").length;
      return len === 10 || len === 11
        ? undefined
        : "전화번호 형식이 올바르지 않습니다.";
    }
    case "bizNum":
      return v.replace(/\D/g, "").length === 10
        ? undefined
        : "사업자등록번호는 10자리 숫자여야 합니다.";
    default:
      return undefined;
  }
}

/** 한 필드의 검증 규칙 (useAdminForm 등에서 사용) */
export interface FieldRule {
  type?: AdminFormFieldType;
  required?: boolean;
  validate?: (value: string) => string | undefined;
}

/**
 * 규칙과 값으로 오류 메시지를 계산한다. (타이밍과 무관한 순수 검증 — 제출 검증용)
 * 순서: 필수 빈 값 → 커스텀 validate → 내장 type 검증
 */
export function getFieldError(
  rule: FieldRule,
  value: string | number | undefined
): string | undefined {
  const raw = String(value ?? "");
  const v = raw.trim();
  if (!v) return rule.required ? "필수 입력 항목입니다." : undefined;
  if (rule.validate) return rule.validate(raw);
  if (rule.type) return validateValue(rule.type, raw);
  return undefined;
}

export interface AdminFormFieldProps {
  /** 필드 라벨 */
  label?: ReactNode;
  /** 라벨과 연결할 컨트롤 id */
  htmlFor?: string;
  /** 필수 표시(*) */
  required?: boolean;
  /** 입력 아래 도움말 */
  description?: ReactNode;
  /** 에러 메시지 (있으면 빨간색으로 표시되고 description 을 대체) */
  error?: ReactNode;
  /** 라벨 위치 (기본 vertical=위) */
  orientation?: "vertical" | "horizontal";
  /** horizontal 일 때 라벨 너비 클래스 (기본 sm:w-28) */
  labelClassName?: string;
  className?: string;

  /** 자동 생성할 입력 종류 (children 미지정 시) */
  type?: AdminFormFieldType;
  /** 자동 입력의 값 */
  value?: string | number;
  /** 자동 입력의 변경 콜백 (comma 는 숫자만 추출한 문자열을 돌려준다) */
  onChange?: (value: string) => void;
  /** 자동 입력의 placeholder */
  placeholder?: string;
  /**
   * 커스텀 검증. 값을 받아 오류 메시지(문자열)를 반환하면 그 메시지가 표시되고,
   * 통과하면 undefined 를 반환한다. (예: 비밀번호 규칙처럼 서비스마다 다른 조건)
   * required 빈 값 검사 이후, 내장 type 검증보다 우선 적용된다.
   * @example validate={(v) => v.length < 8 ? "8자 이상 입력하세요." : undefined}
   */
  validate?: (value: string) => string | undefined;
  /**
   * 제출(저장) 시점에 부모가 true 로 켜면 필수 누락 등 모든 오류를 즉시 표시한다.
   * 평소(false)에는 blur 이후 형식 오류만 표시하고, 필수 빈 값 오류는 띄우지 않는다.
   */
  forceValidate?: boolean;
  /** 자동 입력에 추가로 전달할 input 속성 (type 이 "textarea" | "select" 가 아닐 때) */
  inputProps?: Omit<
    ComponentProps<"input">,
    "value" | "onChange" | "type" | "id" | "placeholder"
  >;
  /** type="textarea" 일 때 표시할 줄 수 (기본 3) */
  rows?: number;
  /** type="select" 일 때 사용할 옵션 목록 */
  options?: AdminSelectOption[];
  /** type="select" 일 때 AdminSelect 에 추가로 전달할 속성 (searchable, clearable 등) */
  selectProps?: Omit<
    AdminSelectProps,
    "options" | "value" | "onChange" | "id" | "placeholder" | "onBlur"
  >;
  /** type="switch" 일 때 Switch 에 추가로 전달할 속성 (size 등) */
  switchProps?: Omit<SwitchProps, "checked" | "onCheckedChange" | "id">;
  /**
   * type="upload" 일 때 AdminImageUpload 에 추가로 전달할 속성 (accept, onUpload, onFilesAdded 등).
   * value/onChange 는 이미지 1장(문자열 URL)만 다룬다 — 여러 장이 필요하면 AdminImageUpload 를 직접 사용한다.
   */
  uploadProps?: Omit<AdminImageUploadProps, "value" | "onChange" | "maxCount">;

  /**
   * 에러가 뜰 때 이 필드로 포커스를 옮길지 여부 (기본 true).
   * 단독 사용 시엔 항상 자기 자신의 에러에 반응해 포커싱한다.
   * `useAdminForm.field()` 로 여러 필드를 한 폼에서 쓸 때는, 제출 시 여러 필드가
   * 동시에 invalid 가 될 수 있어 폼이 "가장 먼저 있는 오류 필드"만 true 로 지정해준다
   * (그 외 필드는 false 로 지정되어 포커스를 가져가지 않는다) — 별도 설정 없이 자동 적용됨.
   */
  autoFocusOnError?: boolean;
  /**
   * (내부용) `useAdminForm` 이 validate() 를 호출할 때마다 증가시키는 값.
   * 같은 필드가 여전히 invalid 인 상태에서 제출을 다시 눌러도 재포커싱되도록 트리거한다.
   */
  focusToken?: number;

  /** 커스텀 컨트롤 (주면 type 자동 생성 대신 이걸 쓴다) */
  children?: ReactNode;
}

export function AdminFormField({
  label,
  htmlFor,
  required,
  description,
  error,
  orientation = "vertical",
  labelClassName,
  className,
  type,
  value,
  onChange,
  placeholder,
  validate,
  forceValidate,
  inputProps,
  rows,
  options,
  selectProps,
  switchProps,
  uploadProps,
  autoFocusOnError = true,
  focusToken,
  children,
}: AdminFormFieldProps) {
  const horizontal = orientation === "horizontal";

  // blur 이후(touched) 또는 제출(forceValidate) 시점부터 검증 결과를 보여준다.
  const [touched, setTouched] = useState(false);

  // 표시할 오류: 부모가 준 error 가 우선, 없으면 아래 규칙으로 자동 검증.
  // - 빈 값 + required: "제출(forceValidate)" 때만 표시 (blur 만으로는 안 띄움)
  // - 값이 있을 때 형식 오류(이메일 등): blur 후 또는 제출 시 표시
  let builtinError: string | undefined;
  if (!children && type) {
    const raw = String(value ?? "");
    const v = raw.trim();
    if (!v) {
      builtinError = required && forceValidate ? "필수 입력 항목입니다." : undefined;
    } else if (touched || forceValidate) {
      builtinError = validate ? validate(raw) : validateValue(type, value);
    }
  }
  const shownError = error ?? builtinError;
  const invalid = shownError != null;

  // 포커스 규칙: autoFocusOnError=false 인 필드는 절대 포커스를 가져가지 않는다
  // (useAdminForm 이 "가장 먼저 있는 오류 필드"에만 true 를 준다 — 나머지는 항상 false).
  // 그 외에는 에러가 "새로 나타난" 순간(false -> true 전이) 또는 focusToken 이
  // 바뀐(=다시 제출을 누른) 순간에 포커싱한다. ref 는 컴포넌트 내부에서만 사용 —
  // 쓰는 쪽(form.field(...))은 아무것도 추가하지 않아도 된다.
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectRef = useRef<AdminSelectHandle>(null);
  const uploadRef = useRef<AdminImageUploadHandle>(null);
  const switchRef = useRef<HTMLButtonElement>(null);
  const wasInvalid = useRef(false);
  const prevFocusToken = useRef(focusToken);
  useEffect(() => {
    const tokenChanged =
      focusToken !== undefined && focusToken !== prevFocusToken.current;
    const newlyInvalid = invalid && !wasInvalid.current;
    if (autoFocusOnError && invalid && (newlyInvalid || tokenChanged)) {
      if (type === "select") selectRef.current?.focus();
      else if (type === "textarea") textareaRef.current?.focus();
      else if (type === "upload") uploadRef.current?.focus();
      else if (type === "switch") switchRef.current?.focus();
      else inputRef.current?.focus();
    }
    wasInvalid.current = invalid;
    prevFocusToken.current = focusToken;
  }, [invalid, focusToken, autoFocusOnError, type]);

  // type 기반 자동 입력 (children 이 없을 때)
  let autoControl: ReactNode = null;
  if (!children && type) {
    if (type === "select") {
      autoControl = (
        <AdminSelect
          ref={selectRef}
          id={htmlFor}
          options={options ?? []}
          value={value != null ? String(value) : ""}
          onChange={(v) => onChange?.(v)}
          placeholder={placeholder}
          onBlur={() => setTouched(true)}
          {...selectProps}
        />
      );
    } else if (type === "textarea") {
      autoControl = (
        <textarea
          ref={textareaRef}
          id={htmlFor}
          rows={rows ?? 3}
          placeholder={placeholder}
          aria-invalid={invalid ? true : undefined}
          onBlur={() => setTouched(true)}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          className="flex w-full min-w-0 appearance-none resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors outline-none placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20"
        />
      );
    } else if (type === "switch") {
      const offOption = options?.[0];
      const onOption = options?.[1];
      const checked = onOption != null && String(value) === String(onOption.value);
      const activeLabel = checked ? onOption?.label : offOption?.label;
      autoControl = (
        <div className="flex items-center gap-2 pt-0.5">
          <Switch
            ref={switchRef}
            id={htmlFor}
            checked={checked}
            onCheckedChange={(next: boolean) => {
              setTouched(true);
              onChange?.(String(next ? onOption?.value : offOption?.value));
            }}
            {...switchProps}
          />
          {activeLabel != null && (
            <span className="text-sm text-muted-foreground">{activeLabel}</span>
          )}
        </div>
      );
    } else if (type === "upload") {
      autoControl = (
        <AdminImageUpload
          ref={uploadRef}
          value={value ? [String(value)] : []}
          onChange={(urls) => onChange?.(urls[0] ?? "")}
          maxCount={1}
          {...uploadProps}
        />
      );
    } else if (FORMATTED_TYPES.includes(type as ColumnFormatType)) {
      autoControl = (
        <Input
          ref={inputRef}
          id={htmlFor}
          inputMode="numeric"
          placeholder={placeholder}
          aria-invalid={invalid ? true : undefined}
          onBlur={() => setTouched(true)}
          value={
            value === "" || value == null
              ? ""
              : formatByType(type as ColumnFormatType, value)
          }
          onChange={(e) => {
            const max = MAX_DIGITS[type as ColumnFormatType];
            let digits = e.target.value.replace(/[^\d]/g, "");
            if (max) digits = digits.slice(0, max);
            onChange?.(digits);
          }}
          {...inputProps}
        />
      );
    } else {
      autoControl = (
        <Input
          ref={inputRef}
          id={htmlFor}
          type={type}
          placeholder={placeholder}
          aria-invalid={invalid ? true : undefined}
          onBlur={() => setTouched(true)}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          {...inputProps}
        />
      );
    }
  }

  const control = children ?? autoControl;

  const labelEl = label ? (
    <Label
      htmlFor={htmlFor}
      className={cn(
        horizontal && "shrink-0 pt-2 sm:w-28",
        !horizontal && "mb-1.5",
        labelClassName
      )}
    >
      {label}
      {required && <span className="ml-0.5 text-destructive">*</span>}
    </Label>
  ) : null;

  const messageEl =
    shownError != null ? (
      <p className="mt-1.5 text-xs text-destructive">{shownError}</p>
    ) : description != null ? (
      <p className="mt-1.5 text-xs text-muted-foreground">{description}</p>
    ) : null;

  if (horizontal) {
    return (
      <div
        className={cn("flex flex-col gap-1.5 sm:flex-row sm:gap-4", className)}
      >
        {labelEl}
        <div className="min-w-0 flex-1">
          {control}
          {messageEl}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", className)}>
      {labelEl}
      {control}
      {messageEl}
    </div>
  );
}
